import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTerminalStore } from '../store/terminalStore'
import { useQueryClient } from '@tanstack/react-query'
import { watchlistApi } from '../api/watchlist'

/**
 * Global keyboard shortcuts — all fire only when not inside an input/textarea.
 *
 * Navigation
 *   /          → Focus search bar
 *   Escape     → Blur input / close modals
 *   1          → /terminal
 *   2          → /portfolio
 *   3          → /watchlist
 *   4          → /settings
 *   5          → /sector  (heatmap)
 *   6          → /earnings
 *   7          → /options
 *   8          → /simulator
 *   9          → /screener
 *   0          → /equity-research
 *
 * Chart — periods
 *   [          → Previous period  (1d ← 5d ← 1mo ← …)
 *   ]          → Next period      (… → 2y → 5y)
 *
 * Chart — intervals
 *   {  (Shift+[) → Previous interval  (1m ← 5m ← 15m ← …)
 *   }  (Shift+]) → Next interval      (… → 1wk → 1mo)
 *
 * Chart — indicators
 *   R          → Toggle RSI
 *   M          → Toggle MACD
 *   B          → Toggle Bollinger Bands
 *   V          → Toggle Volume
 *
 * Chart — type
 *   C          → Candlestick
 *   L          → Line
 *   A          → Area
 *
 * Drawing tools
 *   H          → Toggle horizontal line draw mode
 *   T          → Toggle trendline draw mode
 *   F          → Toggle Fibonacci retracement draw mode
 *   X          → Clear all drawings for active symbol
 *   N          → Exit comparison mode
 *
 * Data
 *   P          → Pause / resume live data
 *   W          → Add / remove active symbol from watchlist
 *
 * General
 *   ?          → Toggle keyboard reference modal
 */

const PERIODS   = ['1d','5d','1mo','3mo','6mo','1y','2y','5y'] as const
const INTERVALS = ['1m','5m','15m','1h','1d','1wk'] as const

export function useKeyboardShortcuts() {
  const navigate     = useNavigate()
  const queryClient  = useQueryClient()
  const {
    toggleIndicator,
    setPeriod, chartPeriod,
    setInterval: setChartInterval, chartInterval,
    setChartType,
    drawMode, setDrawMode, clearDrawings,
    activeSymbol,
    liveData, setLiveData,
    setHotkeyModalOpen, hotkeyModalOpen,
    setComparisonMode,
  } = useTerminalStore()

  const handler = useCallback(
    (e: KeyboardEvent) => {
      const tag     = (e.target as HTMLElement)?.tagName
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable

      // ── Always-on keys ──────────────────────────────────────────────────────
      if (e.key === 'Escape') {
        if (hotkeyModalOpen) { setHotkeyModalOpen(false); return }
        ;(document.activeElement as HTMLElement)?.blur()
        return
      }

      if (e.key === '/' && !isInput) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('bb:focus-search'))
        return
      }

      if (e.key === '?' && !isInput) {
        e.preventDefault()
        setHotkeyModalOpen(!hotkeyModalOpen)
        return
      }

      if (isInput) return   // don't fire nav/chart shortcuts while typing

      // ── Navigation ──────────────────────────────────────────────────────────
      switch (e.key) {
        case '1': navigate('/terminal');   return
        case '2': navigate('/portfolio');  return
        case '3': navigate('/watchlist');  return
        case '4': navigate('/settings');   return
        case '5': navigate('/sector');          return
        case '6': navigate('/earnings');        return
        case '7': navigate('/options');         return
        case '8': navigate('/simulator');       return
        case '9': navigate('/screener');        return
        case '0': navigate('/equity-research'); return
        case 'c': if (e.altKey) { navigate('/crypto');    return } break
        case 's': if (e.altKey) { navigate('/social');    return } break
        case 'g': if (e.altKey) { navigate('/congress');  return } break
        case 'd': if (e.altKey) { navigate('/dark-pool'); return } break
      }

      // ── Chart — periods ──────────────────────────────────────────────────────
      if (e.key === '[') {
        const idx = PERIODS.indexOf(chartPeriod as typeof PERIODS[number])
        if (idx > 0) setPeriod(PERIODS[idx - 1])
        return
      }
      if (e.key === ']') {
        const idx = PERIODS.indexOf(chartPeriod as typeof PERIODS[number])
        if (idx < PERIODS.length - 1) setPeriod(PERIODS[idx + 1])
        return
      }

      // ── Chart — intervals (Shift+[ / Shift+]) ───────────────────────────────
      if (e.key === '{') {
        const idx = INTERVALS.indexOf(chartInterval as typeof INTERVALS[number])
        if (idx > 0) setChartInterval(INTERVALS[idx - 1])
        return
      }
      if (e.key === '}') {
        const idx = INTERVALS.indexOf(chartInterval as typeof INTERVALS[number])
        if (idx < INTERVALS.length - 1) setChartInterval(INTERVALS[idx + 1])
        return
      }

      // ── Chart — indicators ───────────────────────────────────────────────────
      switch (e.key.toLowerCase()) {
        case 'r': toggleIndicator('showRsi');      return
        case 'm': toggleIndicator('showMacd');     return
        case 'b': toggleIndicator('showBollinger');return
        case 'v': toggleIndicator('showVolume');   return
      }

      // ── Chart — type ─────────────────────────────────────────────────────────
      switch (e.key.toLowerCase()) {
        case 'c': setChartType('candlestick'); return
        case 'l': setChartType('line');        return
        case 'a': setChartType('area');        return
      }

      // ── Drawing tools ─────────────────────────────────────────────────────────
      switch (e.key.toLowerCase()) {
        case 'h':
          setDrawMode(drawMode === 'hline' ? 'off' : 'hline')
          return
        case 't':
          setDrawMode(drawMode === 'trendline' ? 'off' : 'trendline')
          return
        case 'f':
          setDrawMode(drawMode === 'fibonacci' ? 'off' : 'fibonacci')
          return
        case 'x':
          clearDrawings(activeSymbol)
          return
        case 'n':
          setComparisonMode(false)
          return
      }

      // ── Live data toggle ──────────────────────────────────────────────────────
      if (e.key.toLowerCase() === 'p') {
        setLiveData(!liveData)
        return
      }

      // ── Watchlist toggle ──────────────────────────────────────────────────────
      if (e.key.toLowerCase() === 'w') {
        const sym = activeSymbol
        // Optimistic: check current watchlist cache then add or remove
        const cached = queryClient.getQueryData<{ symbol: string }[]>(['watchlist']) ?? []
        const isWatched = cached.some((e) => e.symbol === sym)
        if (isWatched) {
          watchlistApi.remove(sym)
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['watchlist'] })
              window.dispatchEvent(new CustomEvent('bb:toast', { detail: { msg: `Removed ${sym} from watchlist`, type: 'neutral' } }))
            })
            .catch(() => {
              window.dispatchEvent(new CustomEvent('bb:toast', { detail: { msg: `Failed to update watchlist`, type: 'error' } }))
            })
        } else {
          watchlistApi.add(sym)
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['watchlist'] })
              window.dispatchEvent(new CustomEvent('bb:toast', { detail: { msg: `Added ${sym} to watchlist`, type: 'success' } }))
            })
            .catch(() => {
              window.dispatchEvent(new CustomEvent('bb:toast', { detail: { msg: `Failed to update watchlist`, type: 'error' } }))
            })
        }
        return
      }
    },
    [
      navigate, queryClient,
      toggleIndicator,
      setPeriod, chartPeriod,
      setChartInterval, chartInterval,
      setChartType,
      drawMode, setDrawMode, clearDrawings,
      activeSymbol,
      liveData, setLiveData,
      setHotkeyModalOpen, hotkeyModalOpen,
      setComparisonMode,
    ]
  )

  useEffect(() => {
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handler])
}
